import { fetchAuthSession } from 'aws-amplify/auth';

export type VariableRecord = {
  variableName: string;
  description: string;
  type: 'string' | 'number' | 'string_array';
  substitutionMode: 'value' | 'component';
  allowedValues: string[];
  defaultValue?: string;
  componentMap?: Record<string, { text: string }>;
  status: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  version?: number;
};

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`
  } as const;
};

const baseUrl = () =>
  process.env.NODE_ENV === 'development'
    ? '/Prod'
    : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');

export const variableService = {
  async list(status?: 'active' | 'archived' | 'draft'): Promise<{ variables: VariableRecord[]; count: number }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const resp = await fetch(`${baseUrl()}/secure/tally-variables${qs}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to list variables (${resp.status})`);
    return await resp.json();
  },
  async get(variableName: string): Promise<VariableRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-variables/${encodeURIComponent(variableName)}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get variable (${resp.status})`);
    return await resp.json();
  },
  async create(payload: Omit<VariableRecord, 'createdAt' | 'updatedAt' | 'version'> & { createdBy?: string }): Promise<VariableRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-variables`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create variable: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async update(variableName: string, payload: Partial<VariableRecord>): Promise<VariableRecord> {
    const resp = await fetch(`${baseUrl()}/secure/tally-variables/${encodeURIComponent(variableName)}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update variable: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async archive(variableName: string): Promise<{ message: string }> {
    const resp = await fetch(`${baseUrl()}/secure/tally-variables/${encodeURIComponent(variableName)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders()
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to archive variable: ${resp.status} ${text}`);
    }
    return await resp.json();
  }
};


